import React, { useEffect, useRef, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { t } from '../i18n';
import Modal from './ui/Modal';
import Button from './ui/Button';

function rolePillText(role) {
  if (role === 'DEALER_SMALL_BLIND') return t('poker.dSb');
  if (role === 'DEALER') return t('poker.d');
  if (role === 'SMALL_BLIND') return t('poker.sb');
  if (role === 'BIG_BLIND') return t('poker.bb');
  return null;
}

function shuffleCopy(list) {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const EDGE = 36;

function scrollerOf(el) {
  return el?.closest('.overflow-y-auto') || el;
}

/**
 * Host seating picker. Same centered ui/Modal chrome as the rest of the app.
 * Title + Shuffle seats + ✕ stay fixed; Deal / Back stay in the footer;
 * the player list scrolls inside the card when it does not fit.
 */
export default function OrderSheet({
  open = true,
  players,
  roleAssignments = {},
  dense = false,
  starting = false,
  error,
  onReorder,
  onShuffle,
  onDeal,
  onBack,
}) {
  const listRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const dragRef = useRef(null);
  const autoScrollRef = useRef(0);
  const playersRef = useRef(players);
  playersRef.current = players;
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = 0;
    }
  };

  const movePlayer = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= players.length) return;
    const copy = [...players];
    const [moved] = copy.splice(index, 1);
    copy.splice(next, 0, moved);
    onReorder(copy);
  };

  const handleShuffle = () => {
    if (starting) return;
    const next = shuffleCopy(players);
    onReorder(next);
    onShuffle?.(next);
  };

  const indexFromY = (clientY) => {
    const list = listRef.current;
    if (!list) return 0;
    const rows = [...list.querySelectorAll('[data-order-row]')];
    if (rows.length === 0) return 0;
    for (let i = 0; i < rows.length; i += 1) {
      const rect = rows[i].getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return rows.length - 1;
  };

  const tickAutoScroll = (clientY) => {
    const list = listRef.current;
    if (!list) return;
    const scroller = scrollerOf(list);
    if (!scroller) return;
    const rect = scroller.getBoundingClientRect();
    let delta = 0;
    if (clientY < rect.top + EDGE) delta = -10;
    else if (clientY > rect.bottom - EDGE) delta = 10;
    if (!delta) {
      stopAutoScroll();
      return;
    }
    const step = () => {
      if (!dragRef.current) {
        stopAutoScroll();
        return;
      }
      scroller.scrollTop += delta;
      autoScrollRef.current = requestAnimationFrame(step);
    };
    if (!autoScrollRef.current) {
      autoScrollRef.current = requestAnimationFrame(step);
    }
  };

  const endDrag = () => {
    const drag = dragRef.current;
    stopAutoScroll();
    if (drag?.move) {
      window.removeEventListener('pointermove', drag.move);
      window.removeEventListener('pointerup', drag.up);
      window.removeEventListener('pointercancel', drag.up);
    }
    dragRef.current = null;
    setDragId(null);
  };

  // endDrag reads refs; this effect is unmount-only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => endDrag(), []);

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.pointerId != null && event.pointerId !== drag.pointerId) return;
    const list = playersRef.current;
    const target = indexFromY(event.clientY);
    if (target !== drag.from && target >= 0 && target < list.length) {
      const copy = [...list];
      const [moved] = copy.splice(drag.from, 1);
      copy.splice(target, 0, moved);
      drag.from = target;
      onReorderRef.current(copy);
    }
    tickAutoScroll(event.clientY);
  };

  const onPointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (event && drag.pointerId != null && event.pointerId !== drag.pointerId) return;
    endDrag();
  };

  const onPointerDown = (event, player, index) => {
    if (starting) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* Playwright / a pointer that isn't capturable */
    }
    const move = (e) => onPointerMove(e);
    const up = (e) => onPointerUp(e);
    dragRef.current = {
      id: player.id,
      from: index,
      pointerId: event.pointerId,
      move,
      up,
    };
    setDragId(player.id);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const rowH = dense ? 44 : 52;

  return (
    <Modal
      open={open}
      onClose={starting ? undefined : onBack}
      dismissible={!starting}
      title={t('order.title')}
      testId="order-sheet"
      headerExtra={(
        <button
          type="button"
          onClick={handleShuffle}
          disabled={starting}
          className="flex-none text-[13px] text-mute underline underline-offset-2 decoration-line hover:text-ink disabled:opacity-40"
          data-testid="order-shuffle"
        >
          {t('order.shuffle')}
        </button>
      )}
      footer={(
        <>
          <Button
            variant="primary"
            size="lg"
            trailingArrow
            onClick={onDeal}
            disabled={!onDeal || players.length < 2 || starting}
            className="flex-1"
            data-testid="order-start"
          >
            {starting ? t('order.starting') : t('lobby.deal')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={onBack}
            disabled={starting}
            className="flex-1"
            data-testid="order-back"
          >
            {t('common.back')}
          </Button>
        </>
      )}
    >
      <p className="text-[14px] text-ink-2 leading-relaxed mb-3">
        {t('order.hint')}
      </p>
      <ul
        ref={listRef}
        className="divide-y divide-line-2 border-y border-line-2"
        data-testid="order-list"
      >
        {players.map((player, index) => {
          const roleText = rolePillText(roleAssignments[player.id]);
          const lifting = dragId === player.id;
          return (
            <li
              key={player.id}
              data-order-row
              data-testid={`order-row-${player.id}`}
              style={{ minHeight: rowH }}
              className={`flex items-center justify-between gap-3 px-2 -mx-2 ${
                lifting ? 'relative z-10 bg-cream-2 rounded-xl shadow-sm' : ''
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-mute text-[13px] tnum w-4 flex-shrink-0">{index + 1}</span>
                <span className="text-ink font-medium truncate">{player.name}</span>
                {roleText && (
                  <span className="text-[10.5px] px-[6px] py-[2.5px] font-semibold tracking-[0.08em] rounded-[4px] border border-line text-mute flex-shrink-0">
                    {roleText}
                  </span>
                )}
              </div>
              <button
                type="button"
                data-testid={`order-handle-${player.id}`}
                aria-label={t('order.reorder', { name: player.name })}
                disabled={starting}
                className="flex-shrink-0 p-2 -mr-1 text-mute hover:text-ink disabled:opacity-30 touch-none"
                onPointerDown={(e) => onPointerDown(e, player, index)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    movePlayer(index, -1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    movePlayer(index, 1);
                  }
                }}
              >
                <GripVertical className="w-5 h-5" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
      {error && (
        <div className="mt-3 text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
    </Modal>
  );
}

export { shuffleCopy };
